import React, { createContext, useContext, useEffect, useState } from "react";
import { baseUrl } from "../services/config";

const ParentContext = createContext();

function getUnreadCount(result, collectionKeys) {
  const payload = result?.payload || result?.data || result;
  const items = Array.isArray(payload)
    ? payload
    : collectionKeys.reduce(
        (records, key) => records || payload?.[key],
        null,
      );

  if (Array.isArray(items)) {
    const hasReadStatus = items.some((item) => item?.is_read !== undefined);
    return hasReadStatus
      ? items.filter((item) => Number(item.is_read) === 0).length
      : items.length;
  }

  const count =
    payload?.unread_count ??
    payload?.unread ??
    payload?.count ??
    result?.unread_count ??
    0;
  return Number(count) || 0;
}

export function ParentProvider({ children }) {
  const storedChildren = JSON.parse(
    localStorage.getItem("parentChildren") || "[]"
  );
  const [childList] = useState(storedChildren);
  const [selectedChild, setSelectedChild] = useState(
    storedChildren[0] || null
  );
  const [unreadStatusCount, setUnreadStatusCount] = useState(0);
  const [unreadResultCount, setUnreadResultCount] = useState(0);
  const [unreadFeeCount, setUnreadFeeCount] = useState(0);
  const [unreadGeneralCount, setUnreadGeneralCount] = useState(0);

  useEffect(() => {
    const studentId =
      selectedChild?.student?.id ||
      selectedChild?.student_id ||
      selectedChild?.id;

    if (!studentId) {
      setUnreadStatusCount(0);
      setUnreadResultCount(0);
      setUnreadFeeCount(0);
      setUnreadGeneralCount(0);
      return undefined;
    }

    const controller = new AbortController();

    async function loadUnreadStatusCount() {
      try {
        const token = localStorage.getItem("parentToken");
        const response = await fetch(
          `${baseUrl}/get_attendance_notifications?student_id=${encodeURIComponent(
            studentId,
          )}`,
          {
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: controller.signal,
          },
        );
        const result = await response.json();
        if (!response.ok) throw new Error("Unable to load unread statuses.");

        setUnreadStatusCount(
          getUnreadCount(result, ["attendance", "notifications"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") setUnreadStatusCount(0);
      }
    }

    async function loadUnreadResultCount() {
      try {
        const token = localStorage.getItem("parentToken");
        const response = await fetch(
          `${baseUrl}/get_result_notifications?student_id=${encodeURIComponent(
            studentId,
          )}`,
          {
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: controller.signal,
          },
        );
        const result = await response.json();
        if (!response.ok) throw new Error("Unable to load result notifications.");

        setUnreadResultCount(
          getUnreadCount(result, ["results", "exam_results", "notifications"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") setUnreadResultCount(0);
      }
    }

    async function loadUnreadFeeCount() {
      try {
        const token = localStorage.getItem("parentToken");
        const response = await fetch(
          `${baseUrl}/get_fee_challan_notifications?student_id=${encodeURIComponent(
            studentId,
          )}`,
          {
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: controller.signal,
          },
        );
        const result = await response.json();
        if (!response.ok) throw new Error("Unable to load fee notifications.");

        setUnreadFeeCount(
          getUnreadCount(result, ["fee_challans", "challans", "notifications"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") setUnreadFeeCount(0);
      }
    }

    async function loadUnreadGeneralCount() {
      try {
        const token = localStorage.getItem("parentToken");
        const response = await fetch(
          `${baseUrl}/get_general_notifications?student_id=${encodeURIComponent(
            studentId,
          )}`,
          {
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: controller.signal,
          },
        );
        const result = await response.json();
        if (!response.ok)
          throw new Error("Unable to load general notifications.");

        setUnreadGeneralCount(
          getUnreadCount(result, ["general_notifications", "notifications"]),
        );
      } catch (error) {
        if (error.name !== "AbortError") setUnreadGeneralCount(0);
      }
    }

    setUnreadStatusCount(0);
    setUnreadResultCount(0);
    setUnreadFeeCount(0);
    setUnreadGeneralCount(0);
    loadUnreadStatusCount();
    loadUnreadResultCount();
    loadUnreadFeeCount();
    loadUnreadGeneralCount();

    return () => controller.abort();
  }, [selectedChild]);

  return (
    <ParentContext.Provider
      value={{
        childList,
        selectedChild,
        setSelectedChild,
        unreadStatusCount,
        setUnreadStatusCount,
        unreadResultCount,
        setUnreadResultCount,
        unreadFeeCount,
        setUnreadFeeCount,
        unreadGeneralCount,
        setUnreadGeneralCount,
      }}
    >
      {children}
    </ParentContext.Provider>
  );
}

export const useParent = () => useContext(ParentContext);
